import { definedMap, withDefault } from "collection-utils";
import flatten from "lodash/flatten";
import type {
    ActionPropertyDescriptor,
    AllowedDataEdits,
    AppDescriptionContext,
    ComponentBuilder,
    ComponentDescriptor,
    ComponentSpecialCaseDescriptor,
    EditedColumnsAndTables,
    InteractiveComponentConfiguratorContext,
    PropertyDescriptor,
    RewritingComponentConfiguratorContext,
    SchemaInspector,
} from ".";
import {
    combineEditedColumnsAndTables,
    convertEditedColumnsToAllowedDataEdits,
    mergeAllowedDataEdits,
    NumberPropertyStyle,
    RequiredKind,
    shouldUseInputContextForTransform,
} from ".";
import type { PrimitiveValue } from "../../common/components/computation-types";
import { isObjectValue, isPrimitiveValue } from "../../common/components/computation-types";
import type { SerializedComponent } from "../../common/components/SerializedApp";
import { AppKind } from "../../common/components/SerializedApp";
import type { AppEnvironment, DataStore, QuotaBannerRequirements, ValueProducer } from "../../common/components/types";
import {
    asBoolean,
    asMaybeString,
    asString,
    isLoadingValue,
    nullLoadingToUndefined,
} from "../../common/computation-model/data";
import { logError } from "../../common/debug-print";
import type {
    ArrayColumnType,
    ArrayContentDescription,
    ArrayItemType,
    ArrayScreenDescription,
    ComponentDescription,
    ComponentKind,
    Description,
    InputOutputTables,
    MutatingScreenKind,
    TableColumn,
    TableGlideType,
    TableRefGlideType,
} from "../../common/description";
import {
    ActionKind,
    ArrayScreenFormat,
    ArrayTransformKind,
    findTable,
    getArrayTransforms,
    getDefaultContextColumnName,
    getTableColumn,
    getTableColumnDisplayName,
    getTableName,
    isComputedColumn,
    isMultiRelationType,
    isPrimitiveType,
    isSingleRelationType,
    makeArrayType,
    makeEmptyComponentDescription,
    makeInputOutputTables,
    makeSourceColumn,
    makeTableRef,
    PropertyKind,
    sheetNameForTable,
} from "../../common/description";
import { getDocURL } from "../../common/docUrl";
import { memoizeFunction } from "../../common/memoize";
import {
    assert,
    defined,
    definedMapWithDefault,
    isEmptyOrUndefined,
    mapFilterUndefined,
    updateDeleteUndefined,
} from "../../common/Support";
import type { InlineListComponentDescription, ListSourcePropertyDescription } from "../../component-utils/inline-list";
import type { WireDataGridColumnKind } from "../../fluent-components/base-components";
import { CardCollection, DataGrid } from "../../fluent-components/fluent-components";
import { WireComponentKind } from "../../wire/component-kind";
import type { WireEditableValue } from "../../wire/types";
import type { ArrayScreenHandler } from "../array-screens";
import { allArrayScreenHandlers, getDefaultArrayScreenHandler, handlerForArrayScreenFormat } from "../array-screens";
import type { ArrayContentHandler, InlineListBuild } from "../array-screens/array-content";
import { PieChartArrayContentHandler } from "../array-screens/charts";
import { ChoiceArrayContentHandler } from "../array-screens/choice";
import { EventPickerArrayContentHandler } from "../array-screens/event-picker";
import { makeSearchedArray, withCaption } from "../builder-utils";
import type { LetBinding, SerializedElementBuilder } from "../serialized-component";
import { spreadComponentID } from "../wire/utils";
import { PopulationMode } from "./description-handlers";
import {
    dynamicFilterColumnPropertyHandler,
    getDefaultCaption,
    makeCaptionStringPropertyDescriptor,
    makeDynamicFilterColumnPropertyHandler,
    makeTableOrRelationPropertyDescriptor,
} from "./descriptor-utils";
import { makeFluentArrayContentHandler } from "./fluent-components-handlers";
import { ComponentHandlerBase } from "./handler";
import { populateDescription } from "./populate-description";
import { NumberPropertyHandler, SwitchPropertyHandler } from "./property-handlers";
import { PropertySection } from "./property-sections";
import {
    getArrayProperty,
    getColumnProperty,
    getEnumProperty,
    getNumberProperty,
    getSourceColumnProperty,
    getStringProperty,
    getSwitchProperty,
    getTableProperty,
    makeActionProperty,
    makeColumnProperty,
    makeEnumProperty,
    makeNumberProperty,
    makeStringProperty,
    makeSwitchProperty,
    makeTableProperty,
} from "./property-utils";
import { getSearchableColumns } from "./searchable-columns";
import type { WireRowComponentHydrator, WireRowComponentInflationBackend, WireValueGetter } from "./wire";

export const ComponentKindInlineList: ComponentKind = "inline-list";

const defaultArrayScreenFormat = ArrayScreenFormat.List;

const allowSearchPropertyHandler = new SwitchPropertyHandler(
    { allowSearch: false },
    "Show search bar",
    PropertySection.Search
);

function makePageSizePropertyHandler(defaultPageSize: number) {
    return new NumberPropertyHandler(
        { pageSize: defaultPageSize },
        "Page size",
        defaultPageSize.toString(),
        RequiredKind.Required,
        NumberPropertyStyle.Entry,
        PropertySection.Options
    );
}

function getArrayItemType(column: TableColumn): ArrayItemType | undefined {
    if (column.type.kind !== "array") {
        logError("Can't make inline list from non-array type");
        return undefined;
    }
    return column.type.items;
}

function getTableRef(column: TableColumn): TableRefGlideType | undefined {
    const items = getArrayItemType(column);
    if (items === undefined) return undefined;

    if (!isSingleRelationType(items)) {
        logError("Can't make inline list from array of non-references");
        return undefined;
    }

    return items;
}

function lookupArrayRefTable(schema: SchemaInspector, column: TableColumn): TableGlideType | undefined {
    return definedMap(getTableRef(column), ref => schema.findTable(ref));
}

export function getPropertyTable(
    t: TableGlideType | undefined,
    d: { readonly propertyName: ListSourcePropertyDescription | undefined },
    s: SchemaInspector
): TableGlideType | undefined {
    const columnProperty = getColumnProperty(d.propertyName);
    if (columnProperty !== undefined) {
        if (t === undefined) {
            logError("Could not find table for class screen");
            return undefined;
        }

        const column = getTableColumn(t, columnProperty);
        if (column === undefined) {
            logError("Could not find column in table for inline list content", columnProperty, getTableName(t));
            return undefined;
        }
        return lookupArrayRefTable(s, column);
    }

    const tableName = getTableProperty(d.propertyName);
    if (tableName !== undefined) {
        return s.findTable(tableName);
    }

    return undefined;
}

function getPropertyTableForContent(
    tables: InputOutputTables,
    d: Description,
    s: SchemaInspector
): TableGlideType | undefined {
    return getPropertyTable(tables.input, d as InlineListComponentDescription, s);
}

function getArrayForDescriptor(
    desc: InlineListComponentDescription | undefined,
    screenContext: ValueProducer | undefined,
    dataStore: DataStore | undefined
): ValueProducer | undefined {
    if (desc === undefined) return undefined;

    const columnProperty = getSourceColumnProperty(desc.propertyName);
    if (columnProperty !== undefined) {
        if (screenContext === undefined) return undefined;

        const columnName = getDefaultContextColumnName(columnProperty);
        if (columnName === undefined) return undefined;

        const { current } = screenContext;
        if (!isObjectValue(current)) return undefined;

        return current[columnName];
    }

    const tableName = getTableProperty(desc.propertyName);
    if (tableName === undefined) return undefined;

    return dataStore?.fetchTableRows(tableName, true);
}

const getApplicableHandlersMemoized = memoizeFunction(
    "getApplicableHandlersMemoized",
    (
        ccc: AppDescriptionContext,
        table: TableGlideType | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined
    ) => {
        const applicableHandlers = allArrayScreenHandlers.filter(a => !a.isLegacy && a.supportsInlineList);
        if (table === undefined) {
            return applicableHandlers;
        }
        return applicableHandlers.filter(
            h =>
                h.defaultContentDescription(
                    {},
                    makeInputOutputTables(table),
                    ccc,
                    true,
                    mutatingScreenKind,
                    false,
                    getPropertyTableForContent,
                    undefined
                ) !== undefined
        );
    }
);

// FIXME: This should return an array of `ArrayContentHandler`s.
function getApplicableHandlers(
    desc: InlineListComponentDescription | undefined,
    column: TableColumn | undefined,
    tables: InputOutputTables | undefined,
    ccc: AppDescriptionContext,
    mutatingScreenKind: MutatingScreenKind | undefined
): ReadonlyArray<ArrayScreenHandler<ArrayContentDescription, ArrayScreenDescription>> {
    if (column === undefined && desc !== undefined && tables !== undefined) {
        column = definedMap(getColumnProperty(desc.propertyName), p => getTableColumn(tables.input, p));
    }

    const table = definedMap(column, c => ccc.findTable(getTableRef(c)));
    let applicableHandlers = getApplicableHandlersMemoized(ccc, table, mutatingScreenKind);
    if (applicableHandlers.length === 0) {
        // FIXME: This is a hack so that we don't have to be able to return
        // an `undefined` result here, which would cause more refactoring.
        applicableHandlers = [getDefaultArrayScreenHandler()];
    }
    return applicableHandlers;
}

export function getMultiRelationDescriptiveName(
    desc: { readonly propertyName: ListSourcePropertyDescription | undefined },
    table: TableGlideType,
    ctx: AppDescriptionContext
): string | undefined {
    let name: string | undefined;
    const columnProperty = getColumnProperty(desc.propertyName);
    if (columnProperty !== undefined) {
        const column = getTableColumn(table, columnProperty);
        if (column === undefined) {
            name = columnProperty;
        } else {
            name = getTableColumnDisplayName(column);
        }
    } else {
        const tableProperty = getTableProperty(desc.propertyName);
        if (tableProperty !== undefined) {
            const appropriateTable = findTable(ctx.schema, tableProperty);
            name = definedMap(appropriateTable, t => sheetNameForTable(t));
        }
    }

    return name;
}

export function isInlineListComponentDescription(desc: ComponentDescription): desc is InlineListComponentDescription {
    return desc.kind === ComponentKindInlineList;
}

export function getInlineListArrayScreenFormat(desc: InlineListComponentDescription): ArrayScreenFormat {
    return withDefault(getEnumProperty(desc.format), defaultArrayScreenFormat);
}

// TODO: Figure out how to do this whenever we reinstate the data grid.
type ColumnDescription = any; // DescriptionOfProperties<typeof dataGridColumnProperties>;

const specialCaseContentHandlers: readonly ArrayContentHandler<ArrayContentDescription>[] = [
    new PieChartArrayContentHandler() as ArrayContentHandler<ArrayContentDescription>,
    new ChoiceArrayContentHandler() as ArrayContentHandler<ArrayContentDescription>,
    new EventPickerArrayContentHandler() as ArrayContentHandler<ArrayContentDescription>,
    makeFluentArrayContentHandler(CardCollection),
    makeFluentArrayContentHandler(DataGrid, (ib, desc, componentID) => {
        const columnDescs = getArrayProperty<ColumnDescription>(desc.columns);
        if (columnDescs === undefined || columnDescs.length === 0) return undefined;

        const { forBuilder } = ib;

        const columns: { title: string; getter: WireValueGetter; kind: WireDataGridColumnKind }[] = [];

        for (const c of columnDescs) {
            const title = getStringProperty(c.title) ?? "";
            const [getter, type] = ib.getValueGetterForProperty(c.value, true);
            if (!isPrimitiveType(type)) continue;
            const kind = type.kind === "boolean" ? "boolean" : type.kind === "image-uri" ? "image-uri" : "string";
            columns.push({ title, getter, kind });
        }

        return hb => {
            const rows = hb.tableScreenContext;
            if (rows.length === 0) return undefined;

            return {
                component: {
                    kind: WireComponentKind.List,
                    ...spreadComponentID(componentID, forBuilder),
                    format: ArrayScreenFormat.DataGrid,
                    componentTitle: "",
                    columns: columns.map(c => ({ title: c.title, kind: c.kind })),
                    items: rows.map(row => {
                        const rhb = hb.makeHydrationBackendForRow(row);

                        const values: (string | boolean)[] = [];
                        for (const c of columns) {
                            let v = nullLoadingToUndefined(c.getter(rhb));
                            if (c.kind === "boolean") {
                                v = asBoolean(v);
                            } else {
                                v = asString(v);
                            }
                            values.push(v);
                        }
                        return values;
                    }),
                },
                isValid: true,
                hasValue: false,
            };
        };
    }),
];

function getArrayContentHandler(format: ArrayScreenFormat): ArrayContentHandler<ArrayContentDescription> {
    let handler = specialCaseContentHandlers.find(h => h.format === format);
    if (handler !== undefined) {
        return handler;
    }
    handler = handlerForArrayScreenFormat(format);
    if (handler === undefined) {
        handler = defined(handlerForArrayScreenFormat(defaultArrayScreenFormat));
    }
    return handler;
}

function getArrayContentHandlerForDescription(
    desc: InlineListComponentDescription | undefined
): ArrayContentHandler<ArrayContentDescription> {
    const kind = definedMapWithDefault(desc, defaultArrayScreenFormat, getInlineListArrayScreenFormat);
    return getArrayContentHandler(kind);
}

function getArrayContentHandlerForSpecialCaseDescriptor(
    ccc: AppDescriptionContext,
    specialCaseDescriptor: ComponentSpecialCaseDescriptor
): ArrayContentHandler<ArrayContentDescription> | undefined {
    return specialCaseContentHandlers.find(
        h => h.getSpecialCaseDescriptors?.(ccc)?.some(d => d.name === specialCaseDescriptor.name) === true
    );
}

type ListSourceItemType<T extends boolean> = T extends true ? TableRefGlideType : ArrayItemType;

function getListSourceArrayType(
    pd: ListSourcePropertyDescription,
    table: TableGlideType
): { column: TableColumn; arrayType: ArrayColumnType; hasFormula: boolean } | undefined {
    const propertyName = getColumnProperty(pd);
    if (propertyName === undefined) return undefined;
    const column = getTableColumn(table, propertyName);
    if (column === undefined) {
        logError(`Could not find column ${propertyName} in table ${getTableName(table).name} for list items`);
        return undefined;
    }

    if (column.type.kind !== "array") return undefined;
    return { column, arrayType: column.type, hasFormula: isComputedColumn(column) };
}

interface ListSourceTypes<T extends boolean> {
    readonly column: TableColumn | undefined;
    readonly arrayType: ArrayColumnType;
    readonly itemsType: ListSourceItemType<T>;
    readonly hasSpecification: boolean;
}

function getListSourceTypes<T extends boolean>(
    pd: ListSourcePropertyDescription,
    table: TableGlideType,
    onlyTableRef: T
): ListSourceTypes<T> | undefined {
    const maybeArrayType = getListSourceArrayType(pd, table);
    if (maybeArrayType === undefined) return undefined;

    const { column, arrayType, hasFormula: hasSpecification } = maybeArrayType;
    const { items } = arrayType;

    if (onlyTableRef) {
        if (!isSingleRelationType(items)) {
            logError("Can't make list from array of non-references");
            return undefined;
        }
    }

    return { column, arrayType, itemsType: items as ListSourceItemType<T>, hasSpecification };
}

export function getListTypes<T extends boolean>(
    schema: SchemaInspector,
    pd: ListSourcePropertyDescription,
    table: TableGlideType,
    onlyTableRef: T
): ListSourceTypes<T> | undefined {
    const propertyName = getColumnProperty(pd);
    const tableName = getTableProperty(pd);

    if (propertyName !== undefined) {
        return getListSourceTypes(pd, table, onlyTableRef);
    } else {
        const t = schema.findTable(tableName);
        if (t === undefined) return undefined;

        const itemsType = makeTableRef(t) as ListSourceItemType<T>;
        return {
            column: undefined,
            itemsType,
            arrayType: makeArrayType(itemsType),
            hasSpecification: false,
        };
    }
}

export function getListTypesAndContext<T extends boolean>(
    builder: ComponentBuilder,
    pd: ListSourcePropertyDescription | undefined,
    table: TableGlideType,
    onlyTableRef: T
): (ListSourceTypes<T> & { ctx: SerializedComponent }) | undefined {
    const types = getListTypes(builder, pd, table, onlyTableRef);
    if (types === undefined) return undefined;

    let ctx: SerializedComponent;

    if (types.column !== undefined) {
        const { b } = builder;
        ctx = b.getProperty(types.column.name, b.getContext, table);
        if (isSingleRelationType(types.itemsType)) {
            ctx = builder.fetchLinkColumn(defined(types.column), ctx, false);
        }
    } else if (isSingleRelationType(types.itemsType)) {
        const t = builder.findTable(types.itemsType);
        if (t === undefined) return undefined;

        ctx = builder.fetchTableRows(getTableName(t), false);
    } else {
        builder.error("No column defined for list component");
        return undefined;
    }

    return { ...types, ctx };
}

export class InlineListComponentHandler extends ComponentHandlerBase<InlineListComponentDescription> {
    constructor() {
        super(ComponentKindInlineList);
    }

    public get appKinds(): AppKind | "both" {
        return AppKind.App;
    }

    public getIsEditor(
        desc: InlineListComponentDescription | undefined,
        ccc: AppDescriptionContext,
        specialCaseDescriptor: ComponentSpecialCaseDescriptor | undefined
    ): boolean {
        let handler: ArrayContentHandler<ArrayContentDescription> | undefined;
        if (desc !== undefined) {
            handler = getArrayContentHandlerForDescription(desc);
        } else if (specialCaseDescriptor !== undefined) {
            handler = getArrayContentHandlerForSpecialCaseDescriptor(ccc, specialCaseDescriptor);
        }
        if (handler === undefined) return false;
        return handler.isEditor;
    }

    public makeNeedsSearch(
        builder: ComponentBuilder,
        desc: InlineListComponentDescription,
        { input }: InputOutputTables
    ): SerializedComponent | undefined {
        const { b } = builder;

        if (getSwitchProperty(desc.allowSearch) !== true) return undefined;

        const data = getListTypesAndContext(builder, desc.propertyName, input, true);
        if (data === undefined) return b.emptyUI;

        return b.isNonEmptyArray(data.ctx);
    }

    public isSearchable(desc: InlineListComponentDescription): boolean {
        return getSwitchProperty(desc.allowSearch) === true;
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: InlineListComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent,
        searchVariable: SerializedComponent | undefined,
        searchNotActive: SerializedComponent | undefined,
        uniqueID: string
    ): SerializedComponent {
        const { b } = builder;

        const data = getListTypesAndContext(builder, desc.propertyName, tables.input, true);
        if (data === undefined) return b.emptyUI;
        const { itemsType, ctx } = data;

        const listTable = builder.findTable(itemsType);
        if (listTable === undefined) return b.emptyUI;

        const contentHandler = getArrayContentHandlerForDescription(desc);

        let array = ctx;
        const lets: LetBinding[] = [];
        if (this.makeNeedsSearch(builder, desc, tables) !== undefined && searchVariable !== undefined) {
            const searchProperties = contentHandler.getBasicSearchProperties(desc);
            const searchedArray = makeSearchedArray(b, builder, array, searchProperties, itemsType, searchVariable);
            if (searchedArray !== undefined) {
                array = searchedArray;
            }
        }

        const inputContextVar = b.getVariable("inputContext");
        lets.push(["inputContext", b.getContext]);

        let outputContextVar: SerializedComponent | undefined;
        // In add/edit/form screens, filters use the output context for the
        // "containing screen" row, which means that even though the list
        // itself might not need the output context, the filter will, at least
        // if it refers to the containing screen.  We're not checking for that
        // here exactly, just whether it is such a screen and there is a
        // filter.
        if (
            contentHandler.needsOutputContext ||
            (!shouldUseInputContextForTransform(mutatingScreenKind) &&
                getArrayTransforms(desc, false, false).some(t => t.kind === ArrayTransformKind.Filter))
        ) {
            outputContextVar = b.getVariable("outputContext");
            lets.push(["outputContext", outputContext]);
        }

        return withCaption(builder, tables.input, desc.caption, lets, caption => {
            const inlineListBuild: InlineListBuild = {
                tables,
                mutatingScreenKind,
                outputContext: outputContextVar,
                inputContext: inputContextVar,
            };
            const processedArray = contentHandler.processArray(
                builder,
                desc,
                listTable,
                b.getContext,
                inlineListBuild,
                uniqueID,
                true
            );
            let content = contentHandler.makeContentForProcessedArray(
                builder,
                desc,
                listTable,
                processedArray,
                definedMap(searchNotActive, s => b.not(s)),
                inlineListBuild,
                uniqueID,
                caption,
                undefined
            );
            if (!contentHandler.supportsEmptyArrays) {
                content = b.ifNonEmptyArray(b.getContext, content);
            }
            return b.bindContextToValue(content, array);
        });
    }

    public needValidation(desc: InlineListComponentDescription): boolean {
        const contentHandler = getArrayContentHandlerForDescription(desc);
        return contentHandler.needValidation(desc);
    }

    public makeValidationFlag(
        b: SerializedElementBuilder,
        desc: InlineListComponentDescription,
        outputContext: SerializedComponent,
        table: TableGlideType
    ): SerializedComponent | undefined {
        const contentHandler = getArrayContentHandlerForDescription(desc);
        return contentHandler.makeValidationFlag(b, desc, outputContext, table);
    }

    public getQuotaBannerRequirements(desc: InlineListComponentDescription): QuotaBannerRequirements {
        const contentHandler = getArrayContentHandlerForDescription(desc);
        return contentHandler.quotaBannerRequirements;
    }

    private getContentPropertyDescriptors(
        desc: ArrayContentDescription | undefined,
        handler: ArrayContentHandler<ArrayContentDescription>,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        array: ValueProducer | undefined
    ): readonly PropertyDescriptor[] {
        return handler.getContentPropertyDescriptors(
            getPropertyTableForContent,
            true,
            desc,
            ccc,
            mutatingScreenKind,
            false,
            true,
            array
        );
    }

    public getDescriptor(
        desc: InlineListComponentDescription | undefined,
        table: TableGlideType | undefined,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        screenContext?: ValueProducer,
        appEnvironment?: AppEnvironment
    ): ComponentDescriptor {
        const applicableHandlers = getApplicableHandlers(
            desc,
            undefined,
            definedMap(table, makeInputOutputTables),
            ccc,
            mutatingScreenKind
        );
        const cases = applicableHandlers.map(h => ({ value: h.format, label: h.label, icon: h.icon }));

        const contentHandler = getArrayContentHandlerForDescription(desc);
        const specialCases = contentHandler.getSpecialCaseDescriptors?.(ccc) ?? [];
        const haveSpecialCases = specialCases.length > 0;

        const contentPropertyDescriptors = this.getContentPropertyDescriptors(
            desc,
            contentHandler,
            ccc,
            mutatingScreenKind,
            getArrayForDescriptor(desc, screenContext, appEnvironment?.dataStore)
        );

        const { sourcePropertyLabel } = contentHandler;
        const properties = [
            makeTableOrRelationPropertyDescriptor(
                mutatingScreenKind,
                false,
                true,
                true,
                sourcePropertyLabel?.[0] ?? "Values",
                sourcePropertyLabel?.[1] ?? PropertySection.Source,
                true,
                { name: "propertyName" }
            ),
        ];
        if (!haveSpecialCases) {
            properties.push({
                kind: PropertyKind.Enum,
                property: { name: "format" },
                label: "Layout",
                menuLabel: "Choose layout",
                cases,
                defaultCaseValue: cases[0].value,
                section: PropertySection.Style,
                visual: "large-images",
                changesDescriptor: true,
            });
        }
        properties.push(...contentPropertyDescriptors);

        if (
            contentHandler.getSupportsSearch(ccc.appKind) &&
            (ccc.appKind !== AppKind.App || (!haveSpecialCases && mutatingScreenKind === undefined))
        ) {
            properties.push(allowSearchPropertyHandler);
        }

        if (contentHandler.supportsDynamicFilter) {
            properties.push(makeDynamicFilterColumnPropertyHandler(getPropertyTableForContent));
        }

        const captionPropertySection = contentHandler.needsCaption(desc);
        if (captionPropertySection !== undefined) {
            properties.unshift(
                makeCaptionStringPropertyDescriptor("List", false, mutatingScreenKind, {
                    propertySection: captionPropertySection,
                })
            );
        }

        properties.push(...this.getBasePropertyDescriptors());

        if (contentHandler.defaultPageSize !== undefined) {
            properties.push(makePageSizePropertyHandler(contentHandler.defaultPageSize));
        }

        const specialCase = specialCases.length === 1 ? specialCases[0] : undefined;

        return {
            name: definedMapWithDefault(specialCase, "Inline List", sc => sc.name),
            description: "An inline collection of related items",
            img: specialCase?.img ?? "componentInlineList",
            group: "Lists",
            // FIXME: special case doc link
            helpUrl: getDocURL(contentHandler.helpPath),
            properties,
            specialCaseDescriptor: specialCase,
        };
    }

    public getActionDescriptors(
        desc: InlineListComponentDescription,
        _table: TableGlideType | undefined,
        schema: SchemaInspector | undefined,
        _mutatingScreenKind: MutatingScreenKind | undefined
    ): readonly ActionPropertyDescriptor[] {
        const contentHandler = getArrayContentHandlerForDescription(desc);
        return contentHandler.getActionDescriptors(desc, getPropertyTableForContent, schema);
    }

    public static defaultComponent(
        column: TableColumn,
        ccc: AppDescriptionContext,
        tables: InputOutputTables
    ): InlineListComponentDescription | undefined {
        const table = ccc.findTable(getTableRef(column));
        if (table === undefined) return undefined;

        const handler = getApplicableHandlers(undefined, column, undefined, ccc, undefined)[0];

        const baseDesc = {
            ...makeEmptyComponentDescription(ComponentKindInlineList),
            format: makeEnumProperty(handler.format),
            propertyName: makeColumnProperty(column.name),
            caption: makeStringProperty(getTableColumnDisplayName(column)),
            allowSearch: makeSwitchProperty(false),
            pageSize: definedMap(handler.defaultPageSize, makeNumberProperty),
        };
        return handler.defaultContentDescription(
            baseDesc,
            tables,
            ccc,
            true,
            undefined,
            false,
            getPropertyTableForContent,
            undefined
        );
    }

    private newComponentFromHandler(
        tables: InputOutputTables,
        propertyName: ListSourcePropertyDescription,
        handler: ArrayContentHandler<ArrayContentDescription>,
        ccc: AppDescriptionContext,
        setCaption: boolean,
        mutatingScreenKind: MutatingScreenKind | undefined,
        specialCaseDescriptor: ComponentSpecialCaseDescriptor | undefined
    ): InlineListComponentDescription | undefined {
        const base = {
            ...makeEmptyComponentDescription(ComponentKindInlineList),
            format: makeEnumProperty(handler.format),
            propertyName,
            allowSearch: makeSwitchProperty(false),
            reverse: makeSwitchProperty(false),
            pageSize: definedMap(handler.defaultPageSize, makeNumberProperty),
        };
        const content = handler.defaultContentDescription(
            base,
            tables,
            ccc,
            true,
            mutatingScreenKind,
            false,
            getPropertyTableForContent,
            specialCaseDescriptor
        );
        if (content === undefined) return undefined;

        let caption: string | undefined;
        if (setCaption) {
            const descriptors = this.getContentPropertyDescriptors(content, handler, ccc, undefined, undefined);
            caption = getDefaultCaption(descriptors, content, tables, ccc);
            if (caption === undefined) {
                caption = sheetNameForTable(tables.input);
            }
        }

        return {
            ...content,
            caption: makeStringProperty(caption),
        };
    }

    private newComponentFromHandlerForAnyTable(
        handler: ArrayContentHandler<ArrayContentDescription>,
        tables: InputOutputTables,
        ccc: AppDescriptionContext,
        setCaption: boolean,
        mutatingScreenKind: MutatingScreenKind | undefined,
        specialCaseDescriptor: ComponentSpecialCaseDescriptor | undefined
    ): InlineListComponentDescription | undefined {
        const { schema } = ccc;
        for (const table of schema.tables) {
            const tableName = getTableName(table);
            if (tableName.isSpecial) continue;

            const desc = this.newComponentFromHandler(
                tables,
                makeTableProperty(tableName),
                handler,
                ccc,
                setCaption,
                mutatingScreenKind,
                specialCaseDescriptor
            );
            if (desc !== undefined) {
                return desc;
            }
        }
        return undefined;
    }

    // This is only here so it's possible to add this component without there being
    // a reference column.
    public newComponent(
        tables: InputOutputTables,
        usedColumns: ReadonlySet<TableColumn>,
        editedColumns: ReadonlySet<TableColumn>,
        iccc: InteractiveComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): InlineListComponentDescription | undefined {
        let desc = super.newComponent(tables, usedColumns, editedColumns, iccc, mutatingScreenKind);
        if (desc?.propertyName !== undefined) return desc;

        const handler = handlerForArrayScreenFormat(defaultArrayScreenFormat);
        if (handler !== undefined) {
            desc = this.newComponentFromHandlerForAnyTable(handler, tables, iccc, true, mutatingScreenKind, undefined);
            if (desc !== undefined) {
                return desc;
            }
        }

        return {
            ...makeEmptyComponentDescription(ComponentKindInlineList),
            format: makeEnumProperty(defaultArrayScreenFormat),
            propertyName: undefined,
            caption: makeStringProperty("Inline list"),
            allowSearch: makeSwitchProperty(false),
            reverse: makeSwitchProperty(false),
            groupByColumn: undefined,
            transforms: [],
            actions: makeActionProperty({ kind: ActionKind.PushDetailScreen }),
            components: [],
            limit: undefined,
            pageSize: definedMap(handler?.defaultPageSize, makeNumberProperty),
        };
    }

    public updateComponent(
        desc: InlineListComponentDescription,
        updates: Partial<InlineListComponentDescription>,
        tables: InputOutputTables,
        ccc: RewritingComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): InlineListComponentDescription {
        const isNewTable = updates.propertyName !== undefined;

        desc = updateDeleteUndefined(desc, updates);
        desc = defined(
            populateDescription(
                d => this.getPropertyAndActionDescriptors(d, tables.input, ccc, mutatingScreenKind),
                isNewTable ? PopulationMode.RewriteWithNewTable : PopulationMode.Rewrite,
                desc,
                desc,
                tables,
                ccc,
                true,
                mutatingScreenKind,
                undefined,
                undefined,
                true
            )
        );

        const handler = getArrayContentHandlerForDescription(desc);
        desc = { ...desc, ...handler.adjustContentDescriptionAfterUpdate(desc, updates, tables, ccc) };
        return desc;
    }

    public getSpecialCaseDescriptors(ccc: AppDescriptionContext): readonly ComponentSpecialCaseDescriptor[] {
        return flatten(specialCaseContentHandlers.map(h => h.getSpecialCaseDescriptors?.(ccc) ?? []));
    }

    public newSpecialCaseComponent(
        specialCaseDescriptor: ComponentSpecialCaseDescriptor,
        tables: InputOutputTables,
        _usedProperties: ReadonlySet<TableColumn>,
        _editedProperties: ReadonlySet<TableColumn>,
        iccc: InteractiveComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): InlineListComponentDescription | undefined {
        const handler = getArrayContentHandlerForSpecialCaseDescriptor(iccc, specialCaseDescriptor);
        if (handler === undefined) return undefined;

        const fromTable = this.newComponentFromHandlerForAnyTable(
            handler,
            tables,
            iccc,
            handler.needsCaption(undefined) !== undefined,
            mutatingScreenKind,
            specialCaseDescriptor
        );
        if (handler.defaultIsTable && fromTable !== undefined) {
            return fromTable;
        }

        for (const column of tables.input.columns) {
            if (!isMultiRelationType(column.type)) continue;
            const table = findTable(iccc.schema, column.type.items);
            if (table === undefined) continue;

            const desc = this.newComponentFromHandler(
                tables,
                makeColumnProperty(column.name),
                handler,
                iccc,
                handler.needsCaption(undefined) !== undefined,
                mutatingScreenKind,
                specialCaseDescriptor
            );
            if (desc !== undefined) {
                return desc;
            }
        }

        return fromTable;
    }

    public getDescriptiveName(
        desc: InlineListComponentDescription,
        tables: InputOutputTables,
        ctx: AppDescriptionContext
    ): [string, string] {
        const name = getMultiRelationDescriptiveName(desc, tables.input, ctx);

        const handler = getArrayContentHandlerForDescription(desc);
        const descriptiveName = handler.getDescriptiveName(desc, getStringProperty(desc.caption), tables.input, name);
        if (descriptiveName !== undefined) {
            return descriptiveName;
        }

        const specialCases = handler.getSpecialCaseDescriptors?.(ctx) ?? [];
        let componentName: string;
        if (specialCases.length === 1) {
            componentName = specialCases[0].name;
        } else {
            componentName = "Inline List";
        }

        if (name === undefined) return [componentName, ""];
        return [componentName, name];
    }

    public getDefaultValues(
        builder: ComponentBuilder,
        desc: InlineListComponentDescription,
        tables: InputOutputTables
    ): readonly [string, SerializedComponent][] {
        const contentHandler = getArrayContentHandlerForDescription(desc);
        if (contentHandler === undefined) return [];

        return contentHandler.getDefaultValues(builder, desc, tables);
    }

    private getColumnsEditedInContent(
        desc: InlineListComponentDescription,
        tables: InputOutputTables,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        withActions: boolean
    ): EditedColumnsAndTables | undefined {
        const contentHandler = getArrayContentHandlerForDescription(desc);
        return contentHandler?.getEditedColumns(
            getPropertyTableForContent,
            desc,
            tables,
            ccc,
            mutatingScreenKind,
            true,
            false,
            withActions
        );
    }

    public getTablesForActions(
        tables: InputOutputTables,
        desc: InlineListComponentDescription,
        schema: SchemaInspector
    ): [InputOutputTables, boolean] | undefined {
        const table = getPropertyTableForContent(tables, desc, schema);
        if (table === undefined) return undefined;
        return [makeInputOutputTables(table), false];
    }

    public getEditedColumns(
        desc: InlineListComponentDescription,
        tables: InputOutputTables,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        withActions: boolean
    ): EditedColumnsAndTables {
        const fromSuper = super.getEditedColumns(desc, tables, ccc, mutatingScreenKind, withActions);
        // FIXME: This will count things twice
        const fromContent = this.getColumnsEditedInContent(desc, tables, ccc, mutatingScreenKind, withActions);
        return combineEditedColumnsAndTables(fromSuper, fromContent);
    }

    public getAdditionalDataEdits(
        desc: InlineListComponentDescription,
        tables: InputOutputTables,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): AllowedDataEdits | undefined {
        const fromSuper = super.getAdditionalDataEdits(desc, tables, ccc, mutatingScreenKind);

        const editedColumns = this.getColumnsEditedInContent(desc, tables, ccc, mutatingScreenKind, true);
        if (editedColumns === undefined) return fromSuper;

        const dataEdits = convertEditedColumnsToAllowedDataEdits(editedColumns.editedColumns, ccc);
        mergeAllowedDataEdits(dataEdits, fromSuper);
        return dataEdits;
    }

    public getScreensUsed(
        desc: InlineListComponentDescription,
        schema: SchemaInspector,
        tables: InputOutputTables | undefined
    ): readonly string[] {
        const handler = getArrayContentHandlerForDescription(desc);
        if (handler === undefined) return [];

        if (tables === undefined) return [];
        const table = getPropertyTable(tables.input, desc, schema);
        if (table === undefined) return [];

        return handler.getScreensUsed(desc, makeInputOutputTables(table), true);
    }

    public rewriteAfterReload(
        desc: InlineListComponentDescription,
        tables: InputOutputTables,
        ccc: RewritingComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isRewrite: boolean
    ): InlineListComponentDescription | undefined {
        const handler = getArrayContentHandlerForDescription(desc);
        if (handler === undefined) return undefined;

        const table = getPropertyTable(tables.input, desc, ccc);
        if (table === undefined) {
            logError("Table for Inline List not found");
            return undefined;
        }

        return handler.rewriteContentAfterReload(
            desc,
            makeInputOutputTables(table),
            d => this.getDescriptor(d, tables.input, ccc, undefined).properties,
            tables,
            ccc,
            mutatingScreenKind,
            isRewrite
        );
    }

    public inflate(
        ib: WireRowComponentInflationBackend,
        desc: InlineListComponentDescription
    ): WireRowComponentHydrator | undefined {
        const handler = getArrayContentHandlerForDescription(desc);
        if (handler === undefined) return undefined;

        const maybeGetter = ib.getValueGetterForTableComponent(desc.propertyName, desc.transforms ?? []);
        if (maybeGetter === undefined) return undefined;

        let captionGetter: WireValueGetter | undefined;
        if (handler.needsCaption(desc) !== undefined) {
            captionGetter = ib.getValueGetterForProperty(desc.caption, true)[0];
        }

        const [tableGetter, contentIB] = maybeGetter;
        const contentHydrator = handler.inflateContent(contentIB, desc, captionGetter, ib, desc.componentID);
        if (contentHydrator === undefined) return undefined;

        let searchPropertyGetters: readonly WireValueGetter[] | undefined;
        if (handler.getSupportsSearch(ib.adc.appKind) && allowSearchPropertyHandler.getSwitch(desc)) {
            const searchableColumns =
                getSearchableColumns(ib.adc).get(getTableName(contentIB.tables.input)) ?? new Set();
            searchPropertyGetters = mapFilterUndefined(Array.from(searchableColumns), p => {
                const result = contentIB.getValueGetterForColumn(makeSourceColumn(p), false, true);
                if (!isPrimitiveType(result[1])) return undefined;
                return result[0];
            });
            if (searchPropertyGetters.length === 0) {
                searchPropertyGetters = undefined;
            }
        }

        let filterValueGetter: WireValueGetter | undefined;
        let filterFormatGetter: WireValueGetter | undefined;
        if (handler.supportsDynamicFilter && dynamicFilterColumnPropertyHandler.getColumnName(desc) !== undefined) {
            const pd = dynamicFilterColumnPropertyHandler.getProperty(desc);
            const [getter, type] = contentIB.getValueGetterForProperty(pd, false);
            if (isPrimitiveType(type)) {
                filterValueGetter = getter;
                filterFormatGetter = contentIB.getValueGetterForProperty(pd, true)[0];
            }
        }

        return hb => {
            // If we exit early and don't get the state before we do, it'll be
            // gone on the next render.
            const pageIndexState = hb.getState("pageIndex", (v): v is number => typeof v === "number", 0);

            let searchBar: WireEditableValue<string> | undefined;
            if (searchPropertyGetters !== undefined) {
                searchBar = hb.getState("search", (v): v is string => typeof v === "string", "");
            }

            let contentHB = tableGetter(hb);
            if (contentHB === undefined || isLoadingValue(contentHB)) return undefined;

            let rows = contentHB.tableScreenContext;
            if (rows.length === 0) return undefined;

            let dynamicFilterNames: readonly [string | null, number][] | undefined;
            let dynamicFilterCurrentIndex: WireEditableValue<number> | undefined;
            if (filterValueGetter !== undefined && filterFormatGetter !== undefined) {
                const filterValuesAndNames: [PrimitiveValue, [string | null, number]][] = [
                    [undefined, [null, rows.length]],
                ];
                const filterEditable = hb.getState("filter", isPrimitiveValue, undefined, true, index => {
                    assert(typeof index === "number");
                    return filterValuesAndNames[index][0];
                });
                const { value: filterValue } = filterEditable;
                let currentIndex = 0;

                const namesForValues = new Map<PrimitiveValue, [string, number]>();
                rows = rows.filter(r => {
                    assert(contentHB !== undefined && !isLoadingValue(contentHB));
                    const rhb = contentHB.makeHydrationBackendForRow(r);
                    const v = filterValueGetter?.(rhb);

                    const isIncluded = filterValue === undefined || v === filterValue;
                    if (v === undefined || !isPrimitiveValue(v)) return isIncluded;

                    const n = asMaybeString(nullLoadingToUndefined(filterFormatGetter?.(rhb)));
                    if (n === undefined) return isIncluded;

                    let entry = namesForValues.get(v);
                    if (entry === undefined) {
                        entry = [n, 1];
                        if (currentIndex === 0 && v === filterValue) {
                            currentIndex = namesForValues.size + 1;
                        }
                    } else {
                        entry = [n, entry[1] + 1];
                    }
                    namesForValues.set(v, entry);

                    return isIncluded;
                });

                filterValuesAndNames.push(...namesForValues);
                dynamicFilterNames = filterValuesAndNames.map(([, n]) => n);
                dynamicFilterCurrentIndex = { value: currentIndex, onChangeToken: filterEditable.onChangeToken };
            }

            if (!isEmptyOrUndefined(searchBar?.value)) {
                const needle = defined(searchBar).value.toLowerCase();
                rows = rows.filter(r => {
                    assert(contentHB !== undefined && !isLoadingValue(contentHB));
                    const rhb = contentHB.makeHydrationBackendForRow(r);
                    for (const g of defined(searchPropertyGetters)) {
                        const v = asMaybeString(nullLoadingToUndefined(g(rhb)));
                        if (v === undefined) continue;
                        if (v.toLowerCase().includes(needle)) return true;
                    }
                    return false;
                });
            }

            let pageSize: number;
            let numPages: number;
            let pageIndex: number;
            if (handler.defaultPageSize !== undefined) {
                const maxPageSize = Math.max(1, getNumberProperty(desc.pageSize) ?? handler.defaultPageSize);
                pageSize = Math.min(rows.length, maxPageSize);
                numPages = Math.ceil(rows.length / pageSize);
                pageIndex = pageIndexState.value;
                if (numPages === 0 || pageIndex < 0) {
                    pageIndex = 0;
                } else if (pageIndex >= numPages) {
                    pageIndex = numPages - 1;
                }

                const firstIndex = pageIndex * pageSize;
                rows = rows.slice(firstIndex, firstIndex + pageSize);
                contentHB = contentHB.withRows(rows);
            } else {
                pageSize = rows.length;
                numPages = 1;
                pageIndex = 0;
            }

            const hydratedContent = contentHydrator(contentHB, hb);
            if (hydratedContent === undefined) return undefined;

            return {
                component: {
                    ...hydratedContent.component,
                    paging: {
                        pageSize,
                        pageIndex: { ...pageIndexState, value: pageIndex },
                        numPages,
                    },
                    searchBar,
                    dynamicFilterNames,
                    dynamicFilterCurrentIndex,
                },
                isValid: hydratedContent.isValid,
                hasValue: hydratedContent.hasValue,
                followUp: hydratedContent.followUp,
            };
        };
    }
}
